import { Modal, Button, Stack, Alert, Group, Text, Loader } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ReactNode } from 'react';

export interface ConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: string;
  loading?: boolean;
  error?: string | null;
  warning?: boolean;
  children?: ReactNode;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
}

export function ConfirmationModal({
  opened,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'red',
  loading = false,
  error = null,
  warning = true,
  children,
  closeOnClickOutside = true,
  closeOnEscape = true,
}: ConfirmationModalProps) {
  const canClose = !loading;
  const effectiveCloseOnClickOutside = closeOnClickOutside && canClose;
  const effectiveCloseOnEscape = closeOnEscape && canClose;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      closeOnClickOutside={effectiveCloseOnClickOutside}
      closeOnEscape={effectiveCloseOnEscape}
      withCloseButton={canClose}
    >
      <Stack gap="md">
        {warning && (
          <Alert
            icon={<IconAlertTriangle size={20} />}
            title="Warning"
            color="orange"
            variant="light"
          >
            {typeof message === 'string' ? (
              <Text size="sm">{message}</Text>
            ) : (
              message
            )}
          </Alert>
        )}

        {!warning && (
          <Text size="sm" c="dimmed">
            {message}
          </Text>
        )}

        {error && (
          <Alert color="red" variant="light">
            <Text size="sm">{error}</Text>
          </Alert>
        )}

        {loading && (
          <Group justify="center" gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Processing...
            </Text>
          </Group>
        )}

        {children}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            color={confirmColor}
            onClick={handleConfirm}
            disabled={loading}
            leftSection={loading ? <Loader size={16} /> : warning ? <IconAlertTriangle size={16} /> : undefined}
          >
            {loading ? 'Processing...' : confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

